import * as React from "react";
import times from "lodash/times";
import styled from "styled-components";

// [MC] Progressive load list items. By default it loads X rows first, then
// when you scroll to the end of that list it requests to load more (displaying a loader at the end
// of the list by default).
//
// You can also choose to lazily load more rows on each idle frame.
//
// This results in a fast initial render and avoids the complicated nature of a virtualised list.
//
// NOTE - requestIdleCallback is currently not supported by safari.

type Props = {
  className?: string;
  idleAmount: number;
  initialAmount: number;
  isActive?: boolean;
  progressiveAmount: number;
  renderItem: (index: number) => any;
  rowCount: number;
  renderLoader?: () => any;
  useWindowScroll?: boolean;
  scrollParent?: React.Ref<HTMLElement>;
};

type State = {
  numRenderRows: number;
};

type StyleProps = {
  visible?: boolean;
};
const Wrapper = styled.div<StyleProps>`
  display: ${(props) => (props.visible ? "block" : "contents")};
`;

class ProgressiveList extends React.Component<Props, State> {
  static defaultProps = {
    className: undefined,
    idleAmount: 0, // load one extra row on idle by default
    initialAmount: 10,
    isActive: true,
    progressiveAmount: 10,
    renderLoader: () => null,
    useWindowScroll: false,
  };
  requestId?: number; // eslint-disable-line react/sort-comp
  ref: any;
  isLoading = false;

  constructor(props: Props) {
    super(props);
    const { rowCount, initialAmount, isActive } = props;
    this.ref = React.createRef();
    this.state = {
      numRenderRows: isActive ? initialAmount : rowCount,
    };
  }

  componentDidMount() {
    if (this.props.scrollParent) {
      this.ref = this.props.scrollParent;
    }
    this.progressivelyLoadMore(false);
    const { useWindowScroll } = this.props;
    const scrollParent = useWindowScroll
      ? window
      : this.ref.current.parentElement;
    scrollParent.addEventListener("scroll", this.handleScroll);
  }

  handleScroll = (e: React.SyntheticEvent) => {
    const { rowCount, progressiveAmount, useWindowScroll } = this.props;
    const { numRenderRows } = this.state;
    let top;
    let height;
    let scrollHeight;
    let reachedLimit;
    if (useWindowScroll && this.ref.current !== null) {
      const boundingClientRect = this.ref.current.getBoundingClientRect();
      top = boundingClientRect.top;
      height = boundingClientRect.height;
      scrollHeight = window.innerHeight;
      reachedLimit = top + height < scrollHeight;
    } else if (e.target instanceof HTMLElement) {
      top = e.target.scrollTop;
      height = e.target.offsetHeight;
      scrollHeight = e.target.scrollHeight;
      reachedLimit = top + height >= scrollHeight;
    }
    if (reachedLimit && numRenderRows !== rowCount && !this.isLoading) {
      this.loadMore(progressiveAmount);
    }
  };

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.rowCount !== this.props.rowCount ||
      prevProps.initialAmount !== this.props.initialAmount
    ) {
      this.initializeList(this.props);
    }
  }

  componentWillUnmount() {
    // @ts-ignore
    if (window.requestIdleCallback) window.cancelIdleCallback(this.requestId);
    const scrollParent = this.props.useWindowScroll
      ? window
      : this.ref.current.parentElement;
    scrollParent.removeEventListener("scroll", this.handleScroll);
  }

  initializeList(props: Props) {
    const { rowCount, isActive, initialAmount } = props;
    // @ts-ignore
    if (window.requestIdleCallback) window.cancelIdleCallback(this.requestId);
    this.setState(
      {
        numRenderRows: isActive ? initialAmount : rowCount,
      },
      () => {
        this.progressivelyLoadMore(false);
      }
    );
  }

  progressivelyLoadMore = (immediateLoad: boolean = true) => {
    const { rowCount, idleAmount } = this.props;
    const { numRenderRows } = this.state;
    if (!window.requestIdleCallback || idleAmount === 0) return;
    if (immediateLoad) this.loadMore(idleAmount);
    if (numRenderRows < rowCount) {
      this.requestId = window.requestIdleCallback(() =>
        this.progressivelyLoadMore()
      );
    }
  };

  loadMore(amount: number) {
    const { rowCount } = this.props;
    if (this.state.numRenderRows >= rowCount) return;
    this.isLoading = true;
    this.setState(
      (state) => ({
        numRenderRows: Math.min(state.numRenderRows + amount, rowCount),
      }),
      () => {
        this.isLoading = false;
      }
    );
  }

  render() {
    const { className, renderItem, scrollParent } = this.props;
    const { numRenderRows } = this.state;
    return (
      <Wrapper
        visible={scrollParent === undefined}
        ref={scrollParent === undefined ? this.ref : null}
        className={className}
      >
        {times(numRenderRows, (i) => renderItem(i))}
      </Wrapper>
    );
  }
}

export default ProgressiveList;
